import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = auth
  const [error, setError] = useState("");

  const persistTokens = (data) => {
    if (data?.access_token) localStorage.setItem("flowtasks_access_token", data.access_token);
    if (data?.refresh_token) localStorage.setItem("flowtasks_refresh_token", data.refresh_token);
  };

  const clearTokens = () => {
    localStorage.removeItem("flowtasks_access_token");
    localStorage.removeItem("flowtasks_refresh_token");
  };

  const checkSession = useCallback(async () => {
    // Skip if no token and no cookie hint — silences 401 noise on /login
    const hasToken = !!localStorage.getItem("flowtasks_access_token");
    const hasCookie = typeof document !== "undefined" && document.cookie.includes("access_token");
    if (!hasToken && !hasCookie) {
      setUser(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      persistTokens(data);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail) || e.message;
      setError(msg);
      return { ok: false, error: msg };
    }
  };

  const register = async (email, password, name) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { email, password, name });
      persistTokens(data);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      const msg = formatApiError(e?.response?.data?.detail) || e.message;
      setError(msg);
      return { ok: false, error: msg };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    clearTokens();
    setUser(false);
  };

  const updateProfile = async (updates) => {
    try {
      const { data } = await api.put("/auth/profile", updates);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiError(e?.response?.data?.detail) };
    }
  };

  return (
    <AuthContext.Provider value={{ user, error, login, register, logout, updateProfile, checkSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
